import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Search, ChevronDown, Check } from "lucide-react";

const MealSelector = ({ 
  meals = [], 
  selectedMeals = [], 
  onMealSelectionChange,
  onClearChanges 
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSelectingMeal, setIsSelectingMeal] = useState(false);
  const dropdownRef = useRef(null);

  // Filter meals based on search term and exclude daily meals
  const filteredMeals = meals.filter(meal => {
    if (meal.isDaily) return false;
    if (!searchTerm) return true;
    return meal.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if we're currently selecting a meal
      if (isSelectingMeal) {
        return;
      }
      
      // Only close if clicking outside the entire dropdown container
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelectingMeal]);

  const handleMealToggle = (meal, event) => {
    // Prevent event propagation and default behavior to avoid closing the dropdown
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent?.stopImmediatePropagation();
    }
    
    // Set flag to prevent dropdown from closing
    setIsSelectingMeal(true);
    
    const isSelected = selectedMeals.some(selected => selected.id === meal.id);
    let newSelectedMeals;
    
    if (isSelected) {
      newSelectedMeals = selectedMeals.filter(m => m.id !== meal.id);
    } else {
      newSelectedMeals = [...selectedMeals, meal];
    }
    
    onMealSelectionChange(newSelectedMeals);
    onClearChanges();
    
    // Reset flag after a short delay
    setTimeout(() => {
      setIsSelectingMeal(false);
    }, 100);
  };

  const handleSelectAll = (event) => {
    // Prevent event propagation and default behavior to avoid closing the dropdown
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent?.stopImmediatePropagation();
    }
    
    // Set flag to prevent dropdown from closing
    setIsSelectingMeal(true);
    
    const allSelected = filteredMeals.every(meal => 
      selectedMeals.some(selected => selected.id === meal.id)
    );
    
    let newSelectedMeals;
    if (allSelected) {
      // Deselect all filtered meals
      newSelectedMeals = selectedMeals.filter(selected => 
        !filteredMeals.some(filtered => filtered.id === selected.id)
      );
    } else {
      // Select all filtered meals
      const newMeals = filteredMeals.filter(meal => 
        !selectedMeals.some(selected => selected.id === meal.id)
      );
      newSelectedMeals = [...selectedMeals, ...newMeals];
    }
    
    onMealSelectionChange(newSelectedMeals);
    onClearChanges();
    
    // Reset flag after a short delay
    setTimeout(() => {
      setIsSelectingMeal(false);
    }, 100);
  };

  const handleClearAll = () => {
    onMealSelectionChange([]);
    onClearChanges();
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t("mealAvailability.selectMeals")}
      </label>
      
      <div className="relative" ref={dropdownRef}>
        {/* Dropdown Button */}
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-md bg-white text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors"
        >
          <div className="flex items-center flex-1">
            {selectedMeals.length > 0 ? (
              <div className="flex flex-col">
                <span className="text-gray-900 font-medium">
                  {selectedMeals.length === 1 
                    ? selectedMeals[0].name
                    : t("mealAvailability.multipleSelected", { count: selectedMeals.length })
                  }
                </span>
                {selectedMeals.length > 1 && (
                  <span className="text-sm text-gray-500">
                    {selectedMeals.slice(0, 2).map(m => m.name).join(", ")}
                    {selectedMeals.length > 2 && ` +${selectedMeals.length - 2} more`}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-gray-500">{t("mealAvailability.chooseMeals")}</span>
            )}
          </div>
          <ChevronDown 
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
              isDropdownOpen ? 'transform rotate-180' : ''
            }`} 
          />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div 
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-[60vh] overflow-hidden"
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("mealAvailability.searchMeals")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t("mealAvailability.multiSelectHint")}
              </p>
            </div>

            {/* Select All / Deselect All */}
            {filteredMeals.length > 0 && (
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={(event) => handleSelectAll(event)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  {filteredMeals.every(meal => 
                    selectedMeals.some(selected => selected.id === meal.id)
                  ) ? t("mealAvailability.deselectAll") : t("mealAvailability.selectAll")}
                </button>
              </div>
            )}

            {/* Meals List */}
            <div className="max-h-48 overflow-y-auto">
              {filteredMeals.length > 0 ? (
                filteredMeals.map((meal) => {
                  const isSelected = selectedMeals.some(selected => selected.id === meal.id);
                  
                  return (
                    <button
                      key={meal.id}
                      type="button"
                      onClick={(event) => handleMealToggle(meal, event)}
                      className={`w-full text-left p-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors border-b border-gray-100 last:border-b-0 ${
                        isSelected ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">{meal.name}</span>
                            {isSelected && (
                              <Check className="h-4 w-4 text-blue-600" />
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm ? t("mealAvailability.noMealsFound") : t("mealAvailability.noMeals")}
                </div>
              )}
            </div>
            
            {/* Close button */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(false)}
                className="w-full text-center text-sm text-gray-600 hover:text-gray-800 py-2"
              >
                {t("mealAvailability.closeDropdown")}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Selected Meals Info */}
      {selectedMeals.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {t("mealAvailability.selectedMeals")}: {selectedMeals.length} {t("mealAvailability.mealsSelected")}
              </p>
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedMeals.map((meal) => (
                  <span
                    key={meal.id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {meal.name}
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleMealToggle(meal, event);
                      }}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                    >
                      <span className="sr-only">{t("mealAvailability.removeMeal")}</span>
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={handleClearAll}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t("mealAvailability.clearAll")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealSelector;
